import { useMemo } from 'react';
import * as d3 from "d3-geo";
import { SiteData } from '../../types';

export interface BubbleData {
  activeSites: number;
  totalSites: number;
  location: string;
  x: number;
  y: number;
  radius: number;
  sessionsLastMonth: number;
  sessions12Months: number;
  isHealthy: boolean;
  sites: SiteData[];
}

// Optimized location coordinates cache
const LOCATION_COORDS: Record<string, [number, number]> = {
  // US Regions
  'NA Mid West': [-87, 40],
  'NA North East': [-74, 41],
  'NA Southeast': [-84, 33],
  'NA Central': [-98, 32],
  'NA West': [-119, 36],
  'NA Mid Atlantic': [-78, 38],
  // Americas
  'Canada': [-106, 56],
  'Mexico': [-102, 24],
  'Brazil': [-47, -14],
  'Argentina': [-64, -34],
  'Chile': [-71, -33],
  'Colombia': [-74, 4],
  'Venezuela': [-66, 8],
  'Venezuela Bolivarian Republic of': [-66, 8],
  'Peru': [-76, -9],
  'Ecuador': [-78, -1],
  'Uruguay': [-56, -33],
  'Paraguay': [-58, -23],
  'Bolivia': [-65, -17],
  // Europe
  'United Kingdom': [-3, 55],
  'France': [2, 46],
  'Germany': [9, 51],
  'Italy': [12, 42],
  'Spain': [-4, 40],
  'Netherlands': [5, 52],
  'Belgium': [4, 51],
  'Switzerland': [8, 47],
  'Austria': [14, 47],
  'Portugal': [-8, 40],
  'Poland': [19, 52],
  'Czech Republic': [15, 50],
  'Hungary': [20, 47],
  'Romania': [25, 46],
  'Bulgaria': [25, 43],
  'Greece': [22, 39],
  'Norway': [9, 61],
  'Sweden': [15, 62],
  'Finland': [26, 64],
  'Denmark': [10, 56],
  'Ireland': [-8, 53],
  'Croatia': [16, 45],
  'Serbia': [21, 44],
  'Slovakia': [19, 49],
  'Slovenia': [15, 46],
  // Asia Pacific
  'Japan': [138, 36],
  'China': [104, 35],
  'India': [77, 20],
  'South Korea': [128, 36],
  'Australia': [133, -27],
  'New Zealand': [174, -41],
  'Thailand': [101, 15],
  'Vietnam': [108, 16],
  'Philippines': [122, 13],
  'Indonesia': [113, -2],
  'Malaysia': [102, 4],
  'Singapore': [104, 1],
  'Taiwan': [121, 24],
  'Hong Kong': [114, 22],
  // Middle East
  'Saudi Arabia': [45, 24],
  'United Arab Emirates': [54, 24],
  'Israel': [35, 31],
  'Turkey': [35, 39],
  'Iran': [53, 32],
  'Iraq': [44, 33],
  'Jordan': [36, 31],
  'Lebanon': [36, 34],
  'Kuwait': [48, 29],
  'Qatar': [51, 25],
  'Bahrain': [50, 26],
  'Oman': [56, 21],
  // Africa
  'South Africa': [22, -30],
  'Egypt': [30, 26],
  'Nigeria': [8, 10],
  'Kenya': [38, -1],
  'Morocco': [-7, 32],
  'Ghana': [-2, 8],
  'Ethiopia': [40, 9],
  'Tanzania': [35, -6],
  'Uganda': [32, 1],
  'Algeria': [1, 28],
  'Tunisia': [9, 34],
  'Libya': [17, 27],
  'Sudan': [30, 15],
  'Cameroon': [12, 7],
  'Ivory Coast': [-5, 8],
};

type CountryFeature = GeoJSON.Feature<GeoJSON.MultiPolygon | GeoJSON.Polygon> & {
  id?: string | number;
  properties: {
    name?: string;
    id?: string | number;
    [k: string]: any;
  };
};

const getLocationCoords = (location: string, countryFeatures: CountryFeature[]): [number, number] | null => {
  // Try predefined coordinates first (fastest)
  if (LOCATION_COORDS[location]) {
    return LOCATION_COORDS[location];
  }
  
  // Fallback: find country in map data and use its centroid
  const countryFeature = countryFeatures.find(c => 
    c.properties?.name?.toLowerCase().includes(location.toLowerCase()) ||
    c.properties?.NAME?.toLowerCase().includes(location.toLowerCase()) ||
    c.properties?.NAME_EN?.toLowerCase().includes(location.toLowerCase())
  );
  
  if (countryFeature && countryFeature.geometry) {
    const centroid = d3.geoCentroid(countryFeature as any);
    return centroid;
  }
  
  return null;
};

export const useMapData = (
  siteData: SiteData[], 
  projection: d3.GeoProjection, 
  countries: CountryFeature[]
) => {
  // Optimized bubble data processing with memoization
  const bubbleData = useMemo(() => {
    if (!siteData.length || !projection) return [];
    
    // Include ALL sites, not just filtered ones
    const locationGroups = new Map<string, {
      activeSites: number;
      totalSites: number;
      location: string;
      sessionsLastMonth: number;
      sessions12Months: number;
      sites: SiteData[];
    }>();
    
    // Process ALL sites for each location
    siteData.forEach(site => {
      const key = site.country === 'United States' ? site.region : site.country;
      
      const existing = locationGroups.get(key);
      if (existing) {
        existing.totalSites++;
        // A site is truly "active" only if it's online AND has >=3 active users
        if (site.isOnline && site.activeUsers >= 3) {
          existing.activeSites++;
        }
        existing.sessionsLastMonth += site.numSessionsLastMonth || 0;
        existing.sessions12Months += site.numSessionsOnServerLast12Month || 0;
        existing.sites.push(site);
      } else {
        locationGroups.set(key, {
          activeSites: (site.isOnline && site.activeUsers >= 3) ? 1 : 0,
          totalSites: 1,
          location: key,
          sessionsLastMonth: site.numSessionsLastMonth || 0,
          sessions12Months: site.numSessionsOnServerLast12Month || 0,
          sites: [site]
        });
      }
    });

    // Convert to array and project coordinates
    const bubbles: BubbleData[] = [];
    for (const group of locationGroups.values()) {
      // Show ALL locations that have sites, regardless of active status
      if (group.totalSites === 0) continue;
      
      const coords = getLocationCoords(group.location, countries);
      if (!coords) continue;
      
      const projected = projection(coords);
      if (!projected) continue;
      
      // Determine if location is healthy based on ratio of truly active sites
      const healthyRatio = group.totalSites > 0 ? group.activeSites / group.totalSites : 0;
      const isHealthy = healthyRatio >= 0.5; // 50% or more truly active sites = healthy
      
      // Use total sites for radius calculation, but ensure minimum visibility
      const radius = Math.max(6, Math.min(40, Math.sqrt(group.totalSites) * 4));
      
      bubbles.push({
        ...group,
        x: projected[0],
        y: projected[1],
        radius,
        isHealthy
      });
    }
    
    return bubbles;
  }, [siteData, projection, countries]);

  return { bubbleData };
};